import { KeyboardEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const SPLIT_REGEX = /[\s,;]+/;

export interface EmailChip {
  email: string;
  isValid: boolean;
  isDuplicate: boolean;
}

interface Props {
  value: EmailChip[];
  onChange: (next: EmailChip[]) => void;
  maxCount?: number;
  disabled?: boolean;
  placeholder?: string;
}

function makeChip(raw: string, existing: EmailChip[]): EmailChip {
  const email = raw.trim().toLowerCase();
  const isValid = EMAIL_REGEX.test(email);
  const isDuplicate = isValid && existing.some((c) => c.email === email && c.isValid);
  return { email, isValid, isDuplicate };
}

const EmailChipsInput = ({
  value,
  onChange,
  maxCount = 50,
  disabled = false,
  placeholder,
}: Props) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [maxReached, setMaxReached] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const remaining = Math.max(0, maxCount - value.length);

  const commitDraft = (raw: string): boolean => {
    const tokens = raw.split(SPLIT_REGEX).filter(Boolean);
    if (tokens.length === 0) return false;

    const additions: EmailChip[] = [];
    let reachedCap = false;
    for (const token of tokens) {
      if (value.length + additions.length >= maxCount) {
        reachedCap = true;
        break;
      }
      const chip = makeChip(token, [...value, ...additions]);
      if (!chip.email) continue;
      additions.push(chip);
    }
    if (additions.length > 0) onChange([...value, ...additions]);
    setMaxReached(reachedCap);
    return additions.length > 0;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab" || e.key === " ") {
      if (draft.trim()) {
        e.preventDefault();
        if (commitDraft(draft)) setDraft("");
      } else if (e.key !== "Tab") {
        e.preventDefault();
      }
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      e.preventDefault();
      const next = value.slice(0, -1);
      onChange(next);
      setMaxReached(false);
    }
  };

  const handleBlur = () => {
    if (draft.trim()) {
      if (commitDraft(draft)) setDraft("");
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    const pasted = e.clipboardData.getData("text");
    if (SPLIT_REGEX.test(pasted)) {
      e.preventDefault();
      if (commitDraft(pasted)) setDraft("");
    }
  };

  const removeAt = (idx: number) => {
    if (disabled) return;
    onChange(value.filter((_, i) => i !== idx));
    setMaxReached(false);
  };

  const focusInput = () => inputRef.current?.focus();

  return (
    <div className="flex flex-col gap-1">
      <div
        onClick={focusInput}
        className={`flex flex-wrap gap-1.5 px-2.5 py-2 min-h-[2.5rem] max-h-[7.5rem] overflow-y-auto border rounded-lg transition-colors ${
          disabled
            ? "border-gray-100 bg-gray-50 cursor-not-allowed"
            : "border-gray-200 bg-white focus-within:border-green-400 focus-within:ring-1 focus-within:ring-green-400 cursor-text"
        }`}
      >
        {value.map((chip, idx) => {
          const invalid = !chip.isValid;
          const dupe = chip.isDuplicate;
          const tone = invalid
            ? "bg-red-50 text-red-600 border border-red-200"
            : dupe
            ? "bg-amber-50 text-amber-700 border border-amber-200"
            : "bg-slate-100 text-gray-700 border border-slate-200";
          const tooltip = invalid
            ? t("buildShare.sendEmail.invalidEmail")
            : dupe
            ? t("buildShare.sendEmail.duplicateEmail")
            : "";
          return (
            <span
              key={`${chip.email}-${idx}`}
              title={tooltip}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] ${tone}`}
            >
              <span className="truncate max-w-[160px]">{chip.email}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(idx);
                }}
                disabled={disabled}
                className="opacity-60 hover:opacity-100 disabled:opacity-30"
                aria-label="remove"
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (maxReached) setMaxReached(false);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          disabled={disabled || remaining === 0}
          placeholder={
            value.length === 0 ? placeholder || t("buildShare.sendEmail.emailsPlaceholder") : ""
          }
          className="flex-1 min-w-[8rem] bg-transparent outline-none text-xs placeholder-gray-300 disabled:cursor-not-allowed"
        />
      </div>
      <div className="flex items-center justify-between text-[10px] text-gray-400">
        <span>{t("buildShare.sendEmail.emailsHint")}</span>
        <span>
          {value.length}/{maxCount}
        </span>
      </div>
      {maxReached && (
        <p className="text-[10px] text-amber-600">
          {t("buildShare.sendEmail.maxEmailsReached", { max: maxCount })}
        </p>
      )}
    </div>
  );
};

export { EMAIL_REGEX };
export default EmailChipsInput;
